import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Chip,
  Typography,
  Tooltip,
  Grid
} from '@mui/material';
import { Edit, Add, Delete, Refresh, Check, Close, Visibility } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import api from '../../../services/api/client';

// Helper to format date for display
const formatDate = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy');
  } catch {
    return dateStr;
  }
};

// Helper to format date with time for display
const formatDateTime = (dateStr) => {
  if (!dateStr) return '-';
  try {
    return format(parseISO(dateStr), 'dd/MM/yyyy HH:mm');
  } catch {
    return dateStr;
  }
};

// Helper to format date for datetime-local input
const toDateTimeLocal = (dateStr) => {
  if (!dateStr) return '';
  try {
    const date = new Date(dateStr);
    return format(date, "yyyy-MM-dd'T'HH:mm");
  } catch {
    return '';
  }
};

// Helper to convert datetime-local value to ISO string
const fromDateTimeLocal = (value) => {
  if (!value) return null;
  try {
    return new Date(value).toISOString();
  } catch {
    return null;
  }
};

export default function PromotionsManagement({ onUpdate }) {
  const [promotions, setPromotions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editDialog, setEditDialog] = useState(false);
  const [createDialog, setCreateDialog] = useState(false);
  const [usesDialog, setUsesDialog] = useState(false);
  const [editingPromo, setEditingPromo] = useState(null);
  const [selectedPromoUses, setSelectedPromoUses] = useState([]);
  const [newPromo, setNewPromo] = useState({
    code: '',
    name: '',
    description: '',
    discount_type: 'percentage',
    discount_value: 10,
    target_type: 'all',
    target_id: null,
    max_uses: null,
    max_uses_per_organization: 1,
    valid_from: new Date().toISOString(),
    valid_until: null,
    is_active: true
  });
  const [saving, setSaving] = useState(false);

  const fetchPromotions = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.get('/admin/credits/promotions');
      setPromotions(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement des promotions');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPromotions();
  }, [fetchPromotions]);

  const handleEdit = (promo) => {
    setEditingPromo({
      ...promo,
      valid_from: promo.valid_from || null,
      valid_until: promo.valid_until || null
    });
    setEditDialog(true);
  };

  const handleViewUses = async (promo) => {
    try {
      const response = await api.get(`/admin/credits/promotions/${promo.id}/uses`);
      setSelectedPromoUses(response.data);
      setUsesDialog(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur de chargement des utilisations');
    }
  };

  const handleSaveEdit = async () => {
    if (!editingPromo) return;

    try {
      setSaving(true);
      await api.patch(`/admin/credits/promotions/${editingPromo.id}`, {
        name: editingPromo.name,
        description: editingPromo.description,
        discount_value: parseFloat(editingPromo.discount_value),
        max_uses: editingPromo.max_uses ? parseInt(editingPromo.max_uses) : null,
        max_uses_per_organization: parseInt(editingPromo.max_uses_per_organization),
        valid_from: editingPromo.valid_from || null,
        valid_until: editingPromo.valid_until || null,
        is_active: editingPromo.is_active
      });
      setEditDialog(false);
      setEditingPromo(null);
      fetchPromotions();
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la mise a jour');
    } finally {
      setSaving(false);
    }
  };

  const handleCreate = async () => {
    try {
      setSaving(true);
      await api.post('/admin/credits/promotions', {
        ...newPromo,
        discount_value: parseFloat(newPromo.discount_value),
        max_uses: newPromo.max_uses ? parseInt(newPromo.max_uses) : null,
        max_uses_per_organization: parseInt(newPromo.max_uses_per_organization),
        valid_from: newPromo.valid_from || null,
        valid_until: newPromo.valid_until || null
      });
      setCreateDialog(false);
      setNewPromo({
        code: '',
        name: '',
        description: '',
        discount_type: 'percentage',
        discount_value: 10,
        target_type: 'all',
        target_id: null,
        max_uses: null,
        max_uses_per_organization: 1,
        valid_from: new Date().toISOString(),
        valid_until: null,
        is_active: true
      });
      fetchPromotions();
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la creation');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (promo) => {
    if (!window.confirm(`Desactiver la promotion "${promo.code}" ?`)) return;

    try {
      await api.delete(`/admin/credits/promotions/${promo.id}`);
      fetchPromotions();
      onUpdate?.();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la suppression');
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          Promotions ({promotions.length})
        </Typography>
        <Box display="flex" gap={1}>
          <Button startIcon={<Add />} variant="contained" onClick={() => setCreateDialog(true)}>
            Nouvelle Promotion
          </Button>
          <Button startIcon={<Refresh />} onClick={fetchPromotions}>
            Actualiser
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'grey.100' }}>
              <TableCell>Code</TableCell>
              <TableCell>Nom</TableCell>
              <TableCell>Reduction</TableCell>
              <TableCell>Cible</TableCell>
              <TableCell align="center">Utilisations</TableCell>
              <TableCell>Validite</TableCell>
              <TableCell align="center">Statut</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {promotions.map((promo) => (
              <TableRow key={promo.id} hover>
                <TableCell>
                  <Chip
                    label={promo.code}
                    color="primary"
                    variant="outlined"
                    sx={{ fontFamily: 'monospace' }}
                  />
                </TableCell>
                <TableCell>
                  <Typography fontWeight="medium">{promo.name}</Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      promo.discount_type === 'percentage'
                        ? `${promo.discount_value}%`
                        : `${promo.discount_value} EUR`
                    }
                    color="success"
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    label={
                      promo.target_type === 'all' ? 'Tous' :
                      promo.target_type === 'plan' ? 'Plan' : 'Pack'
                    }
                    size="small"
                  />
                </TableCell>
                <TableCell align="center">
                  <Typography variant="body2">
                    {promo.current_uses} / {promo.max_uses || '∞'}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    (max {promo.max_uses_per_organization}/org)
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(promo.valid_from)}
                  </Typography>
                  {promo.valid_until && (
                    <Typography variant="caption" color="text.secondary">
                      au {formatDate(promo.valid_until)}
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="center">
                  {promo.is_valid ? (
                    <Chip label="Valide" color="success" size="small" />
                  ) : promo.is_active ? (
                    <Chip label="Expire" color="warning" size="small" />
                  ) : (
                    <Chip label="Inactif" color="default" size="small" />
                  )}
                </TableCell>
                <TableCell align="center">
                  <Tooltip title="Voir utilisations">
                    <IconButton size="small" onClick={() => handleViewUses(promo)}>
                      <Visibility fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Modifier">
                    <IconButton size="small" onClick={() => handleEdit(promo)}>
                      <Edit fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Desactiver">
                    <IconButton size="small" color="error" onClick={() => handleDelete(promo)}>
                      <Delete fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit Dialog */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Modifier la Promotion</DialogTitle>
        <DialogContent>
          {editingPromo && (
            <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
              <TextField
                label="Code"
                value={editingPromo.code || ''}
                disabled
                fullWidth
              />
              <TextField
                label="Nom"
                value={editingPromo.name || ''}
                onChange={(e) => setEditingPromo({ ...editingPromo, name: e.target.value })}
                fullWidth
              />
              <TextField
                label="Description"
                value={editingPromo.description || ''}
                onChange={(e) => setEditingPromo({ ...editingPromo, description: e.target.value })}
                fullWidth
                multiline
                rows={2}
              />
              <TextField
                label="Valeur reduction"
                type="number"
                value={editingPromo.discount_value || 0}
                onChange={(e) => setEditingPromo({ ...editingPromo, discount_value: e.target.value })}
                fullWidth
                inputProps={{ step: 0.01, min: 0.01 }}
              />
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    label="Max utilisations (vide = illimite)"
                    type="number"
                    value={editingPromo.max_uses || ''}
                    onChange={(e) => setEditingPromo({ ...editingPromo, max_uses: e.target.value })}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    label="Max par organisation"
                    type="number"
                    value={editingPromo.max_uses_per_organization || 1}
                    onChange={(e) => setEditingPromo({ ...editingPromo, max_uses_per_organization: e.target.value })}
                    fullWidth
                    inputProps={{ min: 1 }}
                  />
                </Grid>
              </Grid>
              <TextField
                label="Valide a partir de"
                type="datetime-local"
                value={toDateTimeLocal(editingPromo.valid_from)}
                onChange={(e) => setEditingPromo({ ...editingPromo, valid_from: fromDateTimeLocal(e.target.value) })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="Valide jusqu'au (optionnel)"
                type="datetime-local"
                value={toDateTimeLocal(editingPromo.valid_until)}
                onChange={(e) => setEditingPromo({ ...editingPromo, valid_until: fromDateTimeLocal(e.target.value) })}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={editingPromo.is_active || false}
                    onChange={(e) => setEditingPromo({ ...editingPromo, is_active: e.target.checked })}
                  />
                }
                label="Promotion active"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)} startIcon={<Close />}>
            Annuler
          </Button>
          <Button
            onClick={handleSaveEdit}
            variant="contained"
            disabled={saving}
            startIcon={saving ? <CircularProgress size={20} /> : <Check />}
          >
            Enregistrer
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nouvelle Promotion</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Code (ex: PROMO2025)"
              value={newPromo.code}
              onChange={(e) => setNewPromo({ ...newPromo, code: e.target.value.toUpperCase() })}
              fullWidth
              required
            />
            <TextField
              label="Nom"
              value={newPromo.name}
              onChange={(e) => setNewPromo({ ...newPromo, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Description"
              value={newPromo.description}
              onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })}
              fullWidth
              multiline
              rows={2}
            />
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <FormControl fullWidth>
                  <InputLabel>Type reduction</InputLabel>
                  <Select
                    value={newPromo.discount_type}
                    label="Type reduction"
                    onChange={(e) => setNewPromo({ ...newPromo, discount_type: e.target.value })}
                  >
                    <MenuItem value="percentage">Pourcentage (%)</MenuItem>
                    <MenuItem value="fixed_amount">Montant fixe (EUR)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Valeur"
                  type="number"
                  value={newPromo.discount_value}
                  onChange={(e) => setNewPromo({ ...newPromo, discount_value: e.target.value })}
                  fullWidth
                  inputProps={{ step: 0.01, min: 0.01 }}
                />
              </Grid>
            </Grid>
            <FormControl fullWidth>
              <InputLabel>Cible</InputLabel>
              <Select
                value={newPromo.target_type}
                label="Cible"
                onChange={(e) => setNewPromo({ ...newPromo, target_type: e.target.value })}
              >
                <MenuItem value="all">Tous (plans et packs)</MenuItem>
                <MenuItem value="plan">Plans uniquement</MenuItem>
                <MenuItem value="pack">Packs uniquement</MenuItem>
              </Select>
            </FormControl>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <TextField
                  label="Max utilisations"
                  type="number"
                  value={newPromo.max_uses || ''}
                  onChange={(e) => setNewPromo({ ...newPromo, max_uses: e.target.value })}
                  fullWidth
                  inputProps={{ min: 1 }}
                  helperText="Vide = illimite"
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  label="Max par org"
                  type="number"
                  value={newPromo.max_uses_per_organization}
                  onChange={(e) => setNewPromo({ ...newPromo, max_uses_per_organization: e.target.value })}
                  fullWidth
                  inputProps={{ min: 1 }}
                />
              </Grid>
            </Grid>
            <TextField
              label="Valide a partir de"
              type="datetime-local"
              value={toDateTimeLocal(newPromo.valid_from)}
              onChange={(e) => setNewPromo({ ...newPromo, valid_from: fromDateTimeLocal(e.target.value) })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Valide jusqu'au (optionnel)"
              type="datetime-local"
              value={toDateTimeLocal(newPromo.valid_until)}
              onChange={(e) => setNewPromo({ ...newPromo, valid_until: fromDateTimeLocal(e.target.value) })}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newPromo.is_active}
                  onChange={(e) => setNewPromo({ ...newPromo, is_active: e.target.checked })}
                />
              }
              label="Promotion active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)} startIcon={<Close />}>
            Annuler
          </Button>
          <Button
            onClick={handleCreate}
            variant="contained"
            disabled={saving || !newPromo.code || !newPromo.name}
            startIcon={saving ? <CircularProgress size={20} /> : <Add />}
          >
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Uses Dialog */}
      <Dialog open={usesDialog} onClose={() => setUsesDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Historique d'utilisation</DialogTitle>
        <DialogContent>
          {selectedPromoUses.length === 0 ? (
            <Typography color="text.secondary" py={4} textAlign="center">
              Aucune utilisation pour cette promotion
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Organisation</TableCell>
                    <TableCell>Utilisateur</TableCell>
                    <TableCell align="right">Montant initial</TableCell>
                    <TableCell align="right">Reduction</TableCell>
                    <TableCell align="right">Montant final</TableCell>
                    <TableCell>Date</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {selectedPromoUses.map((use) => (
                    <TableRow key={use.id}>
                      <TableCell>{use.organization_name || use.organization_id}</TableCell>
                      <TableCell>{use.user_email || '-'}</TableCell>
                      <TableCell align="right">{use.original_amount?.toFixed(2)} EUR</TableCell>
                      <TableCell align="right" sx={{ color: 'success.main' }}>
                        -{use.discount_amount?.toFixed(2)} EUR
                      </TableCell>
                      <TableCell align="right" fontWeight="bold">
                        {use.final_amount?.toFixed(2)} EUR
                      </TableCell>
                      <TableCell>
                        {formatDateTime(use.used_at)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUsesDialog(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
